import React, {
  useContext, useMemo, useState,
} from 'react';
import { useMutation } from '@apollo/client';
import {
  Button, Checkbox, Header, Segment,
} from 'semantic-ui-react';
import { SCHEDULE_EXPERT_WIZA_UPDATES } from '../../queries/expert';
import DataContext from '../../contexts/DataContext';
import MessageContext from '../../contexts/MessageContext';
import ExpertWizaUpdateSelection, { hasLinkedInUrl } from './ExpertWizaUpdateSelection';
import ExpertCards from './ExpertCards';

const ExpertCardsWithWizaControls = ({ experts }) => {
  const { user } = useContext(DataContext);
  const { setSuccessPopup, setErrorPopup } = useContext(MessageContext);
  const [selectedExpertIds, setSelectedExpertIds] = useState([]);
  const [updateRecentlyUpdatedExperts, setUpdateRecentlyUpdatedExperts] = useState(false);
  const [bulkSelection, setBulkSelection] = useState(false);
  const eligibleExpertIds = useMemo(
    () => experts.filter((expert) => hasLinkedInUrl(expert)).map((expert) => expert.id),
    [experts],
  );
  const [scheduleExpertWizaUpdates, { loading: scheduling }] = useMutation(
    SCHEDULE_EXPERT_WIZA_UPDATES,
    {
      onCompleted: ({ scheduleExpertWizaUpdates: scheduledExperts }) => {
        setSelectedExpertIds([]);
        setUpdateRecentlyUpdatedExperts(false);
        setBulkSelection(false);
        setSuccessPopup({
          title: 'Wiza updates scheduled',
          content: `Scheduled Wiza updates for ${scheduledExperts.length} expert(s). This may take a few minutes. Refresh the page to check for updates.`,
        });
      },
      onError: (err) => {
        setErrorPopup({
          title: 'Error scheduling Wiza updates',
          content: err.message,
        });
      },
    },
  );

  const selectedExpertIdSet = new Set(selectedExpertIds);
  const selectedCount = selectedExpertIds.length;
  const eligibleCount = eligibleExpertIds.length;

  const toggleExpertSelection = (expertId) => {
    setBulkSelection(false);
    setSelectedExpertIds((currentExpertIds) => (
      currentExpertIds.includes(expertId)
        ? currentExpertIds.filter((idToKeep) => idToKeep !== expertId)
        : currentExpertIds.concat(expertId)
    ));
  };

  const toggleEligibleSelection = () => {
    if (bulkSelection) {
      setSelectedExpertIds([]);
      setBulkSelection(false);
      return;
    }

    setSelectedExpertIds(eligibleExpertIds);
    setUpdateRecentlyUpdatedExperts(false);
    setBulkSelection(true);
  };

  const scheduleSelectedExperts = () => {
    scheduleExpertWizaUpdates({
      variables: {
        expertIds: selectedExpertIds,
        forceUpdate: bulkSelection ? false : updateRecentlyUpdatedExperts,
      },
    });
  };

  return (
    <>
      <Segment className="wiza-update-controls">
        <Header as="h4">
          Wiza profile updates
          <Header.Subheader>
            {`${eligibleCount} experts with LinkedIn URLs are eligible for Wiza updates.`}
          </Header.Subheader>
        </Header>
        <div className="wiza-update-actions">
          <Button
            basic={!bulkSelection}
            primary={bulkSelection}
            size="small"
            content={bulkSelection ? 'Unselect selections' : `Select all eligible (${eligibleCount})`}
            disabled={eligibleCount === 0 || scheduling}
            onClick={toggleEligibleSelection}
          />
          <Checkbox
            checked={updateRecentlyUpdatedExperts}
            label="Update even if recently updated"
            disabled={selectedCount === 0 || bulkSelection || scheduling}
            onChange={() => setUpdateRecentlyUpdatedExperts((value) => !value)}
          />
          <Button
            primary
            size="small"
            loading={scheduling}
            disabled={selectedCount === 0 || scheduling}
            content={`Schedule Wiza update (${selectedCount})`}
            onClick={scheduleSelectedExperts}
          />
        </div>
      </Segment>
      <ExpertCards
        experts={experts}
        renderControls={(expert) => (
          <ExpertWizaUpdateSelection
            expert={expert}
            selected={selectedExpertIdSet.has(expert.id)}
            onChange={toggleExpertSelection}
            timeZone={user.timeZone}
          />
        )}
      />
    </>
  );
};

export default ExpertCardsWithWizaControls;
